import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';
import { api } from '../api/client.js';

export default function EditApp() {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Track whether a key was originally set (we never display the value itself,
  // just whether one exists). The form's anthropicApiKeyId starts as null and
  // is only populated when the user explicitly enters a new value or removes it.
  const [hasExistingKey, setHasExistingKey] = useState(false);
  const [keyAction, setKeyAction] = useState('keep'); // 'keep' | 'replace' | 'remove'

  useEffect(() => {
    api.get('/api/apps')
      .then((r) => {
        const a = r.data.apps.find((x) => x.id === id);
        if (!a) { setError('App not found'); return; }
        // Fix 5: backend now sends hasAnthropicKey (boolean) instead of the actual ID for non-admins.
        // Admins still get the full ID, but we never display it — just whether it's set.
        setHasExistingKey(!!a.hasAnthropicKey);
        setForm({
          name: a.name,
          description: a.description,
          url: a.url,
          category: a.category,
          authType: a.authType,
          iconColor: a.iconColor,
          iconLabel: a.iconLabel,
          anthropicApiKeyId: '',  // never pre-fill the existing key
          awsResourceTag: a.awsResourceTag || '',
          pricing: {
            inputPerMillion: a.pricing?.inputPerMillion || 0,
            outputPerMillion: a.pricing?.outputPerMillion || 0,
            perCall: a.pricing?.perCall || ''
          }
        });
      })
      .catch((e) => setError(e.message));
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updatePricing = (k, v) => setForm((f) => ({ ...f, pricing: { ...f.pricing, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        pricing: {
          inputPerMillion: parseFloat(form.pricing.inputPerMillion) || 0,
          outputPerMillion: parseFloat(form.pricing.outputPerMillion) || 0,
          perCall: form.pricing.perCall ? parseFloat(form.pricing.perCall) : null
        }
      };

      // Only send anthropicApiKeyId if user explicitly took an action.
      // 'keep' → omit the field entirely so backend leaves it untouched
      // 'replace' → send the new value
      // 'remove' → send empty string (backend will clear it)
      if (keyAction === 'keep') {
        delete payload.anthropicApiKeyId;
      } else if (keyAction === 'remove') {
        payload.anthropicApiKeyId = '';
      }
      // else 'replace' → keep the value the user typed

      await api.patch(`/api/apps/${id}`, payload);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!form) {
    return (
      <>
        <Navbar />
        <main className="max-w-xl mx-auto px-6 py-8">
          {error ? <p className="text-red-600">{error}</p> : <p className="text-muted">Loading…</p>}
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="max-w-xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-medium mb-1">Edit app</h1>
        <p className="text-sm text-muted mb-6">Update settings, pricing, or Anthropic key mapping.</p>

        <form onSubmit={submit} autoComplete="off" className="bg-white border border-border rounded-xl p-6 space-y-4">
          <Field label="Name">
            <input required maxLength={60} className="input" value={form.name}
              onChange={(e) => update('name', e.target.value)} />
          </Field>
          <Field label="Description">
            <input maxLength={200} className="input" value={form.description}
              onChange={(e) => update('description', e.target.value)} />
          </Field>
          <Field label="App URL">
            <input required type="url" className="input" value={form.url}
              onChange={(e) => update('url', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select className="input" value={form.category} onChange={(e) => update('category', e.target.value)}>
                <option>Generative</option><option>Productivity</option>
                <option>Analysis</option><option>Chat</option><option>Custom</option>
              </select>
            </Field>
            <Field label="Auth type">
              <select className="input" value={form.authType} onChange={(e) => update('authType', e.target.value)}>
                <option value="SSO">SSO</option>
                <option value="SEPARATE">Separate login</option>
                <option value="APIKEY">API key</option>
                <option value="NONE">No auth</option>
              </select>
            </Field>
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-sm font-medium mb-1">Pricing</p>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Input $/1M">
                <input type="number" step="0.01" min="0" className="input"
                  value={form.pricing.inputPerMillion}
                  onChange={(e) => updatePricing('inputPerMillion', e.target.value)} />
              </Field>
              <Field label="Output $/1M">
                <input type="number" step="0.01" min="0" className="input"
                  value={form.pricing.outputPerMillion}
                  onChange={(e) => updatePricing('outputPerMillion', e.target.value)} />
              </Field>
              <Field label="Per-call $">
                <input type="number" step="0.001" min="0" className="input"
                  value={form.pricing.perCall}
                  onChange={(e) => updatePricing('perCall', e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <Field label="Anthropic API key ID">
              <KeyField
                hasExisting={hasExistingKey}
                action={keyAction}
                onActionChange={setKeyAction}
                newValue={form.anthropicApiKeyId}
                onNewValueChange={(v) => update('anthropicApiKeyId', v)}
              />
              <p className="text-[11px] text-muted mt-1">
                Auto-syncs Anthropic usage every 10 min when set.
              </p>
            </Field>
          </div>

          <div className="border-t border-border pt-4">
            <Field label="AWS resource tag (subapp)">
              <input
                className="input font-mono"
                placeholder="e.g. doc-analyzer"
                value={form.awsResourceTag}
                onChange={(e) => update('awsResourceTag', e.target.value)}
                autoComplete="off"
              />
              <p className="text-[11px] text-muted mt-1">
                Value of the <code>subapp</code> tag on this app's AWS resources, for cost attribution.
                Leave blank if the app isn't AWS-hosted (external SaaS, browser-only, etc.).
              </p>
            </Field>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => navigate('/')} className="text-sm px-4 py-2 rounded-lg hover:bg-neutral-50">
              Cancel
            </button>
            <button disabled={submitting} className="text-sm bg-ink text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </main>
    </>
  );
}

/**
 * Write-only field for the Anthropic API key ID.
 * - If a key already exists: shows "✓ Key configured" with Replace and Remove buttons.
 *   The actual value is NEVER displayed.
 * - If no key exists, or the user clicked Replace: shows a plain input.
 * - The "Remove" action is reversible until you click Save changes.
 */
function KeyField({ hasExisting, action, onActionChange, newValue, onNewValueChange }) {
  // No key set yet → plain input
  if (!hasExisting) {
    return (
      <input
        className="input font-mono"
        placeholder="apikey_01..."
        value={newValue}
        onChange={(e) => onNewValueChange(e.target.value)}
        autoComplete="off"
      />
    );
  }

  // Key exists, user wants to keep it → show status + actions
  if (action === 'keep') {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
        <span className="text-sm text-emerald-800">
          <span className="font-medium">Key configured</span>
          <span className="text-emerald-700 ml-2">— hidden for security</span>
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onActionChange('replace')}
            className="text-xs text-emerald-800 hover:text-emerald-900 underline"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onActionChange('remove')}
            className="text-xs text-red-700 hover:text-red-800 underline"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  // Key exists, user clicked Replace → plain input + Cancel
  if (action === 'replace') {
    return (
      <div>
        <div className="flex gap-2">
          <input
            className="input flex-1 font-mono"
            placeholder="apikey_01..."
            value={newValue}
            onChange={(e) => onNewValueChange(e.target.value)}
            autoFocus
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => { onActionChange('keep'); onNewValueChange(''); }}
            className="text-xs text-muted hover:text-ink px-3 border border-border rounded-lg hover:bg-neutral-50"
          >
            Cancel
          </button>
        </div>
        <p className="text-[11px] text-amber-700 mt-1">
          Will replace the existing key on save.
        </p>
      </div>
    );
  }

  // Key exists, user clicked Remove → confirmation banner + Undo
  if (action === 'remove') {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
        <span className="text-sm text-red-800">
          <span className="font-medium">Key will be removed</span>
          <span className="text-red-700 ml-2">on save</span>
        </span>
        <button
          type="button"
          onClick={() => onActionChange('keep')}
          className="text-xs text-red-800 hover:text-red-900 underline"
        >
          Undo
        </button>
      </div>
    );
  }

  return null;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-muted mb-1.5">{label}</span>
      {children}
    </label>
  );
}
